import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  QrCode, Gift, Star, ArrowRight, Lightning,
  Users, Storefront, Sparkle,
  ArrowsClockwise, Crown, Phone
} from '@phosphor-icons/react';
import GlobalNav from '../../components/GlobalNav';
import { useLanguage } from '../../contexts/LanguageContext';

const HowItWorksPublic = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('consumer');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const consumerSteps = [
    {
      step: '01',
      icon: Users,
      title: t('howitworks_c_step_1'),
      description: t('howitworks_c_step_1_desc'),
      detail: t('howitworks_c_step_1_detail'),
      color: '#C3A365'
    },
    {
      step: '02',
      icon: QrCode,
      title: t('howitworks_c_step_2'),
      description: t('howitworks_c_step_2_desc'),
      detail: t('howitworks_c_step_2_detail'),
      color: '#145A3A'
    },
    {
      step: '03',
      icon: Star,
      title: t('howitworks_c_step_3'),
      description: t('howitworks_c_step_3_desc'),
      detail: t('howitworks_c_step_3_detail'),
      color: '#C3A365'
    },
    {
      step: '04',
      icon: ArrowsClockwise,
      title: t('howitworks_c_step_4'),
      description: t('howitworks_c_step_4_desc'),
      detail: t('howitworks_c_step_4_detail'),
      color: '#145A3A'
    },
    {
      step: '05',
      icon: Gift,
      title: t('howitworks_c_step_5'),
      description: t('howitworks_c_step_5_desc'),
      detail: t('howitworks_c_step_5_detail'),
      color: '#C3A365'
    }
  ];

  const merchantSteps = [
    {
      step: '01',
      icon: Phone,
      title: t('howitworks_m_step_1'),
      description: t('howitworks_m_step_1_desc'),
      detail: t('howitworks_m_step_1_detail'),
      color: '#C3A365'
    },
    {
      step: '02',
      icon: QrCode,
      title: t('howitworks_m_step_2'),
      description: t('howitworks_m_step_2_desc'),
      detail: t('howitworks_m_step_2_detail'),
      color: '#145A3A'
    },
    {
      step: '03',
      icon: Users,
      title: t('howitworks_m_step_3'),
      description: t('howitworks_m_step_3_desc'),
      detail: t('howitworks_m_step_3_detail'),
      color: '#C3A365'
    },
    {
      step: '04',
      icon: Sparkle,
      title: t('howitworks_m_step_4'),
      description: t('howitworks_m_step_4_desc'),
      detail: t('howitworks_m_step_4_detail'),
      color: '#145A3A'
    }
  ];

  const faqs = [
    { q: t('howitworks_faq_1_q'), a: t('howitworks_faq_1_a') },
    { q: t('howitworks_faq_2_q'), a: t('howitworks_faq_2_a') },
    { q: t('howitworks_faq_3_q'), a: t('howitworks_faq_3_a') },
    { q: t('howitworks_faq_4_q'), a: t('howitworks_faq_4_a') },
    { q: t('howitworks_faq_5_q'), a: t('howitworks_faq_5_a') }
  ];

  const steps = activeTab === 'consumer' ? consumerSteps : merchantSteps;

  return (
    <div className="min-h-screen bg-[#0F1A14] text-white">
      <GlobalNav />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-[#1E2F26] border border-[#C3A365]/30 px-4 py-2 rounded-full mb-8"
          >
            <Lightning size={16} className="text-[#C3A365]" weight="fill" />
            <span className="text-sm text-[#C3A365]">{t('howitworks_badge')}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl sm:text-6xl font-light tracking-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t('howitworks_title')}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-[#8BA89A] max-w-2xl mx-auto"
          >
            {t('howitworks_subtitle')}
          </motion.p>
        </div>
      </section>

      {/* Tab Selector */}
      <section className="px-6 pb-16">
        <div className="max-w-md mx-auto">
          <div className="flex bg-[#1E2F26] p-1.5 rounded-xl">
            {[
              { id: 'consumer', label: t('howitworks_tab_consumer'), icon: Users },
              { id: 'merchant', label: t('howitworks_tab_merchant'), icon: Storefront }
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-all ${
                    isActive
                      ? 'bg-[#C3A365] text-[#0F1A14]'
                      : 'text-[#8BA89A] hover:text-white'
                  }`}
                  data-testid={`howitworks-tab-${tab.id}`}
                >
                  <Icon size={18} weight={isActive ? 'fill' : 'regular'} />
                  <span className="text-sm">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              {steps.map((item, i) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="relative"
                  >
                    {/* Connector */}
                    {i < steps.length - 1 && (
                      <div className="absolute left-10 top-24 w-0.5 h-16 bg-gradient-to-b from-[#C3A365]/50 to-transparent hidden sm:block" />
                    )}

                    <div className="flex gap-6 p-6 bg-[#1E2F26]/50 border border-[#283D32] hover:border-[#C3A365]/30 transition-all">
                      {/* Step number */}
                      <div className="hidden sm:flex flex-col items-center">
                        <div 
                          className="w-20 h-20 rounded-2xl flex items-center justify-center"
                          style={{ backgroundColor: `${item.color}15`, border: `1px solid ${item.color}30` }}
                        >
                          <Icon size={32} style={{ color: item.color }} weight="duotone" />
                        </div>
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-[#C3A365] text-sm font-bold">{item.step}</span>
                          <h3 className="text-xl font-medium text-white">{item.title}</h3>
                        </div>
                        <p className="text-white mb-2">{item.description}</p>
                        <p className="text-[#8BA89A] text-sm">{item.detail}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Points Value */}
      <section className="px-6 py-24 bg-[#18261E]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('howitworks_points_title')}
            </h2>
          </div>

          <div className="bg-[#1E2F26] border border-[#283D32] p-8 sm:p-12 text-center">
            <div className="flex items-center justify-center gap-6 sm:gap-10 mb-8">
              <div>
                <span className="text-6xl sm:text-7xl font-light text-white">100</span>
                <p className="text-[#8BA89A] mt-2">{t('howitworks_points_pts')}</p>
              </div>
              <span className="text-4xl text-[#C3A365]">=</span>
              <div>
                <span className="text-6xl sm:text-7xl font-light text-[#C3A365]">£1</span>
                <p className="text-[#8BA89A] mt-2">{t('howitworks_points_credit')}</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-6 text-left">
              {[
                { icon: Star, text: t('howitworks_points_tip_1') },
                { icon: Crown, text: t('howitworks_points_tip_2') },
                { icon: Gift, text: t('howitworks_points_tip_3') }
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <Icon size={20} className="text-[#C3A365]" weight="duotone" />
                    <span className="text-sm text-[#8BA89A]">{item.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 py-24 bg-[#0F1A14]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#C3A365] text-sm tracking-[0.2em] uppercase font-semibold mb-4">
              {t('howitworks_faq_badge')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('howitworks_faq_title')}
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                viewport={{ once: true }}
                className="border border-[#283D32] overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  className="w-full p-5 flex items-center justify-between text-left hover:bg-[#1E2F26]/50 transition-colors"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-medium text-white pr-4">{faq.q}</span>
                  <motion.span
                    animate={{ rotate: expandedFaq === i ? 180 : 0 }}
                    className="text-[#C3A365] flex-shrink-0"
                  >
                    ▼
                  </motion.span>
                </button>
                <AnimatePresence>
                  {expandedFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-5 text-[#8BA89A] leading-relaxed border-t border-[#283D32] pt-4">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-24 bg-gradient-to-b from-[#18261E] to-[#0F1A14]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('howitworks_cta_title')}
          </h2>
          <p className="text-[#8BA89A] mb-8">
            {t('howitworks_cta_desc')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <motion.button
              onClick={() => navigate('/activer')}
              className="px-8 py-4 bg-[#C3A365] text-[#0F1A14] font-semibold hover:bg-[#D4B576] transition-colors flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="howitworks-cta-btn"
            >
              {t('howitworks_cta_btn')} <ArrowRight size={18} />
            </motion.button>
            <motion.button
              onClick={() => navigate('/reseau')}
              className="px-8 py-4 border border-[#3E5248] text-white font-medium hover:border-[#C3A365]/50 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="howitworks-merchants-btn"
            >
              {t('howitworks_cta_merchants')}
            </motion.button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#1E2F26]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="brand-logo text-xl text-[#8BA89A]">habitué</span>
          <p className="text-xs text-[#8BA89A]">© 2025 habitué. {t('footer_rights')}</p>
        </div>
      </footer>
    </div>
  );
};

export default HowItWorksPublic;
